import { fetchAllRows } from './fetchAllRows';
import { isMissingAccountColumn } from './dbErrors';

// Reading, writing and deleting spreadsheet uploads. `client` is the Supabase client (passed in so tests can fake it).
//
// WHERE UPLOADS WRITE
//   jobs          one row per job: id {user}_manual_{batch}_{n}, source 'manual', upload_batch_id = the batch.
//   transactions  revenue rows with id {user}_uprev_{batch}_{n}, expense rows {user}_upexp_{batch}_{n}.
//                 (Uploads made before this change used a timestamp in place of the batch id.)
//   upload_batches one history row per upload that added something.
//   manual_expenses / manual_revenue are NOT written by uploads (they hold what the owner types in Canopy), but
//   they have an upload_batch_id column, so deleting an upload also clears any row tagged with that batch.
// Every query below filters by contractor_id. QuickBooks rows and Canopy-entered rows are never matched.
export const BATCH_TOKEN = '{BATCH}';
const CHUNK = 100;
const WRITE_CHUNK = 500;
const MISSING_TABLE = new Set(['42P01', 'PGRST205']);

const chunks = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };
const msg = (e) => (e && e.message) || 'unknown error';

export const uploadIds = (userId) => ({
  job: i => `${userId}_manual_${BATCH_TOKEN}_${i}`,
  revenue: i => `${userId}_uprev_${BATCH_TOKEN}_${i}`,
  expense: i => `${userId}_upexp_${BATCH_TOKEN}_${i}`,
});

// This business's earlier uploaded jobs and uploaded money rows, every page.
export async function loadExistingUploads(client, userId) {
  const jobs = await fetchAllRows(() => client.from('jobs')
    .select('id,name,client_name,job_type,status,upload_batch_id')
    .eq('contractor_id', userId).eq('source', 'manual').not('upload_batch_id', 'is', null));
  if (jobs.error) return { error: jobs.error };
  const txns = [];
  let withCategory = true;
  for (const prefix of [`${userId}_uprev_`, `${userId}_upexp_`]) {
    const read = (cols) => fetchAllRows(() => client.from('transactions')
      .select(cols).eq('contractor_id', userId).like('id', `${prefix}%`));
    let res = await read('id,job_id,type,amount,txn_date,description,doc_number,vendor,account_name');
    if (res.error && isMissingAccountColumn(res.error)) { // the labor update is not applied yet: no categories
      withCategory = false;
      res = await read('id,job_id,type,amount,txn_date,description,doc_number,vendor');
    }
    if (res.error) return { error: res.error };
    res.data.filter(t => t.id.startsWith(prefix)).forEach(t => txns.push(t));
  }
  return { jobs: jobs.data, txns, withCategory };
}

// Writes a plan. Safe to run again after a failure: the next preview matches whatever was already saved.
// If it stops part-way, the history row is corrected to what was really saved (or removed if nothing was).
export async function applyUpload(client, userId, fileName, plan) {
  const { jobInserts, jobUpdates, txnInserts, txnUpdates, summary } = plan;
  let batchId = null;
  if (jobInserts.length || txnInserts.length) {
    const { data: batch, error } = await client.from('upload_batches').insert({
      contractor_id: userId, filename: fileName,
      job_count: summary.jobs.added, revenue_count: summary.revenue.added,
      expense_count: summary.expenses.added, template_version: 'v1',
    }).select().single();
    if (error) throw new Error(error.message);
    batchId = batch.id;
  }
  const fill = (v) => (typeof v === 'string' ? v.split(BATCH_TOKEN).join(batchId) : v);
  const written = { jobs: 0, revenue: 0, expenses: 0 };
  const stripCategory = (rows) => rows.map(({ account_name, ...rest }) => rest); // eslint-disable-line no-unused-vars

  try {
    for (const part of chunks(jobInserts, WRITE_CHUNK)) {
      const rows = part.map(j => ({ ...j, id: fill(j.id), contractor_id: userId, source: 'manual', upload_batch_id: batchId }));
      const { error } = await client.from('jobs').insert(rows);
      if (error) throw new Error(error.message);
      written.jobs += rows.length;
    }
    let dropCategory = false;
    for (const part of chunks(txnInserts, WRITE_CHUNK)) {
      let rows = part.map(t => ({ ...t, id: fill(t.id), job_id: fill(t.job_id), contractor_id: userId }));
      if (dropCategory) rows = stripCategory(rows);
      let { error } = await client.from('transactions').insert(rows);
      if (error && isMissingAccountColumn(error)) { // labor update not applied yet: save without categories
        dropCategory = true;
        ({ error } = await client.from('transactions').insert(stripCategory(rows)));
      }
      if (error) throw new Error(error.message);
      written.revenue += rows.filter(r => r.type === 'revenue').length;
      written.expenses += rows.filter(r => r.type === 'expense').length;
    }
    for (const u of jobUpdates) {
      const { error } = await client.from('jobs').update(u.patch).eq('id', u.id).eq('contractor_id', userId);
      if (error) throw new Error(error.message);
    }
    for (const u of txnUpdates) {
      let patch = u.patch;
      if (dropCategory) { const { account_name, ...rest } = patch; patch = rest; } // eslint-disable-line no-unused-vars
      if (!Object.keys(patch).length) continue;
      let { error } = await client.from('transactions').update(patch).eq('id', u.id).eq('contractor_id', userId);
      if (error && isMissingAccountColumn(error)) {
        dropCategory = true;
        const { account_name, ...rest } = patch; // eslint-disable-line no-unused-vars
        if (Object.keys(rest).length) ({ error } = await client.from('transactions').update(rest).eq('id', u.id).eq('contractor_id', userId));
        else error = null;
      }
      if (error) throw new Error(error.message);
    }
  } catch (err) {
    if (batchId) {
      if (written.jobs + written.revenue + written.expenses === 0) {
        await client.from('upload_batches').delete().eq('id', batchId).eq('contractor_id', userId);
      } else {
        await client.from('upload_batches').update({ job_count: written.jobs, revenue_count: written.revenue, expense_count: written.expenses })
          .eq('id', batchId).eq('contractor_id', userId);
      }
    }
    throw err;
  }
}

// Removes one upload. Returns { ok, removed: { transactions, jobs, saved }, keptJobs, problems: [text] }.
// Nothing is removed that is not from this business and this upload. A job that now also has QuickBooks,
// Canopy-entered or later-upload entries is kept (and reported) so those entries are never orphaned.
export async function deleteUploadBatch(client, userId, batchId) {
  const problems = [];
  const removed = { transactions: 0, jobs: 0, saved: 0 };
  let keptJobs = 0;

  const jobsRes = await fetchAllRows(() => client.from('jobs').select('id').eq('contractor_id', userId).eq('upload_batch_id', batchId));
  if (jobsRes.error) return { ok: false, removed, keptJobs, problems: [`Could not read this upload's jobs (${msg(jobsRes.error)}).`] };
  const jobIds = jobsRes.data.map(j => j.id);

  // Money rows of this upload: new-style ids carry the batch id; older ones are found through the upload's jobs.
  const txnIds = new Set();
  let readFailed = false;
  for (const p of ['uprev', 'upexp']) {
    const prefix = `${userId}_${p}_${batchId}_`;
    const res = await fetchAllRows(() => client.from('transactions').select('id').eq('contractor_id', userId).like('id', `${prefix}%`));
    if (res.error) { readFailed = true; problems.push(`Could not read this upload's ${p === 'uprev' ? 'revenue' : 'expense'} rows (${msg(res.error)}).`); continue; }
    res.data.forEach(t => { if (t.id.startsWith(prefix)) txnIds.add(t.id); });
  }
  const legacy = new RegExp(`^${userId.replace(/[^\w-]/g, '')}_(uprev|upexp)_\\d+_\\d+$`);
  for (const part of chunks(jobIds, CHUNK)) {
    const res = await fetchAllRows(() => client.from('transactions').select('id').eq('contractor_id', userId).in('job_id', part));
    if (res.error) { readFailed = true; problems.push(`Could not read the rows on this upload's jobs (${msg(res.error)}).`); break; }
    res.data.forEach(t => { if (legacy.test(t.id)) txnIds.add(t.id); });
  }

  let txnDeleteFailed = false;
  for (const part of chunks([...txnIds], CHUNK)) {
    const { error } = await client.from('transactions').delete().eq('contractor_id', userId).in('id', part);
    if (error) { txnDeleteFailed = true; problems.push(`Could not remove some revenue or expense rows (${msg(error)}).`); break; }
    removed.transactions += part.length;
  }

  let savedFailed = false;
  for (const table of ['manual_expenses', 'manual_revenue']) {
    const { error } = await client.from(table).delete().eq('contractor_id', userId).eq('upload_batch_id', batchId);
    if (error && !MISSING_TABLE.has(error.code)) { savedFailed = true; problems.push(`Could not clear saved entries from this upload (${msg(error)}).`); }
  }

  // Jobs: only after the rows above are gone, and only the ones nothing else points at.
  if (readFailed || txnDeleteFailed || savedFailed) {
    if (jobIds.length) problems.push('The upload\'s jobs were left in place until the rows above are removed.');
  } else {
    const inUse = new Set();
    let checkFailed = false;
    for (const part of chunks(jobIds, CHUNK)) {
      for (const table of ['transactions', 'labor_entries', 'manual_expenses', 'manual_revenue']) {
        const res = await fetchAllRows(() => client.from(table).select('id,job_id').eq('contractor_id', userId).in('job_id', part));
        if (res.error) {
          if (MISSING_TABLE.has(res.error.code)) continue;
          checkFailed = true; problems.push(`Could not check whether jobs are still in use (${msg(res.error)}).`); break;
        }
        res.data.forEach(r => inUse.add(r.job_id));
      }
      if (checkFailed) break;
    }
    if (!checkFailed) {
      const deletable = jobIds.filter(id => !inUse.has(id));
      keptJobs = jobIds.length - deletable.length;
      for (const part of chunks(deletable, CHUNK)) {
        const { error } = await client.from('jobs').delete().eq('contractor_id', userId).eq('upload_batch_id', batchId).in('id', part);
        if (error) { problems.push(`Could not remove some jobs (${msg(error)}).`); break; }
        removed.jobs += part.length;
      }
    }
  }

  if (problems.length === 0) {
    const { error } = await client.from('upload_batches').delete().eq('id', batchId).eq('contractor_id', userId);
    if (error) problems.push(`Could not remove this upload from your history (${msg(error)}).`);
  }
  return { ok: problems.length === 0, removed, keptJobs, problems };
}

export function deleteResultMessage(r) {
  if (r.ok) {
    const kept = r.keptJobs ? ` ${r.keptJobs} job${r.keptJobs === 1 ? ' was' : 's were'} kept because other entries (from QuickBooks, from you, or from another upload) are on ${r.keptJobs === 1 ? 'it' : 'them'}.` : '';
    return `Upload deleted. ${r.removed.jobs} job${r.removed.jobs === 1 ? '' : 's'} and ${r.removed.transactions} revenue and expense row${r.removed.transactions === 1 ? '' : 's'} removed.${kept}`;
  }
  return `Not everything could be removed. ${r.problems.join(' ')} Nothing else was changed. Trying again is safe: choose Delete on this upload again and it will pick up where it stopped.`;
}
